import { IconButton, Stack, Typography, useTheme } from '@mui/material';

import { useResponsive } from 'src/hooks/use-responsive';

import CustomAutocomplete from '../custom-autocomplete/custom-autocomplete';
import { ConfirmDialog } from '../custom-dialog';
import Iconify from '../iconify';
import { useAppHeader } from './app-header.hook';

export interface AppHeaderProps {
  title?: string;
  subtitle?: string;
  handleOpenDrawer?: () => void;
}

export default function AppHeader({ title, subtitle, handleOpenDrawer }: AppHeaderProps) {
  const { modal, onClickLogout, onClickConfirmLogout, handleSelectOption } = useAppHeader();
  const theme = useTheme();
  const { searchOptions } = useAppHeader();
  const responsive = useResponsive;

  const isXs = responsive('down', 'sm');
  const isSm = responsive('down', 'md');
  const isMd = responsive('down', 'lg');

  function getTypography(xs: string, sm: string, md: string, xl: string) {
    if (isXs) return xs;
    if (isSm) return sm;
    if (isMd) return md;
    return xl;
  }

  const greetingTypography = getTypography('bold-hd-xs', 'bold-hd-sm', 'bold-hd-md', 'bold-hd-xl');
  const titleTypography = getTypography('header-xs', 'header-sm', 'header-md', 'header-xl');
  const subtitleTypography = getTypography('body-xs', 'body-sm', 'body-md', 'body-xl');

  return (
    <header data-testid="appHeader-render-test">
      <Stack direction="row">
        {isMd && (
          <IconButton
            onClick={handleOpenDrawer}
            size="small"
            disableRipple
            sx={{
              ':hover': { bgcolor: 'transparent' },
              mr: 1,
              ml: isXs ? 1 : 2,
              mt: isXs ? 3 : 0,
              mb: isMd ? 2 : 0,
              alignSelf: isXs ? 'start' : 'center',
            }}
          >
            <Iconify icon="ri:menu-line" />
          </IconButton>
        )}
        {isXs ? (
          <Stack width="100%" justifyContent="space-between" alignItems="center" pr={2} py={3} pl={0} spacing={1}>
            <Stack width="100%" direction="row" justifyContent="space-between">
              <Stack direction="row" alignItems="flex-start" justifyContent="flex-start" spacing={1}>
                <Iconify
                  color={theme.palette.primary.main}
                  icon="ri:account-circle-line"
                  width={isSm ? 28 : 36}
                  height={isSm ? 28 : 36}
                  mt={0.3}
                />
                <Stack>
                  <Stack direction="row" spacing={0.5}>
                    <Typography typography={greetingTypography}>Olá,</Typography>
                    <Typography typography={titleTypography} noWrap>
                      {title}
                    </Typography>
                  </Stack>
                  <Typography typography={subtitleTypography}>{subtitle}</Typography>
                </Stack>
              </Stack>
              <IconButton
                onClick={onClickLogout}
                size="small"
                disableRipple
                sx={{ ':hover': { bgcolor: 'transparent' }, color: (theme) => theme.palette.primary.main, p: 0 }}
              >
                <Iconify icon="ri:logout-box-r-line" width={24} height={24} />
              </IconButton>
            </Stack>

            <Stack width="90%">
              <CustomAutocomplete
                placeholder="Faça a sua busca rápida"
                options={searchOptions}
                handleSelectOption={handleSelectOption}
                noOptionsText="Nenhum resultado encontrado"
              />
            </Stack>
          </Stack>
        ) : (
          <Stack width="100%" justifyContent="space-between" alignItems="center" p={4} pl={isMd ? 1 : 4} direction="row">
            <Stack width="50%">
              <Stack spacing={1} width="100%">
                <Stack direction="row" alignItems="flex-start" justifyContent="flex-start" spacing={1}>
                  <Iconify
                    color={theme.palette.primary.main}
                    icon="ri:account-circle-line"
                    width={isSm ? 28 : 36}
                    height={isSm ? 28 : 36}
                    mt={0.3}
                  />
                  <Stack>
                    <Stack direction="row" spacing={0.5}>
                      <Typography typography={greetingTypography}>Olá,</Typography>
                      <Typography typography={titleTypography}>{title}</Typography>
                    </Stack>
                    <Typography typography={subtitleTypography}>{subtitle}</Typography>
                  </Stack>
                </Stack>
              </Stack>
            </Stack>

            <Stack width="50%">
              <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={2} width="100%">
                <Stack width="85%">
                  <CustomAutocomplete
                    placeholder="Faça a sua busca rápida"
                    options={searchOptions}
                    handleSelectOption={handleSelectOption}
                    noOptionsText="Nenhum resultado encontrado"
                  />
                </Stack>
                <Stack alignItems="end">
                  <IconButton
                    onClick={onClickLogout}
                    size="small"
                    disableRipple
                    sx={{ ':hover': { bgcolor: 'transparent' }, color: (theme) => theme.palette.primary.main }}
                  >
                    <Iconify icon="ri:logout-box-r-line" width={24} height={24} />
                    {isXs ? null : (
                      <Typography typography={isSm ? 'body-sm' : 'body-md'} ml={1}>
                        Sair
                      </Typography>
                    )}
                  </IconButton>
                </Stack>
              </Stack>
            </Stack>
          </Stack>
        )}
      </Stack>
      {modal.value && (
        <ConfirmDialog
          icon="question-line"
          open={modal.value}
          onClose={modal.onFalse}
          title="Sair"
          content="Você tem certeza que deseja sair?"
          onConfirm={onClickConfirmLogout}
          confirmText="Sair"
          cancelText="Cancelar"
        />
      )}
    </header>
  );
}
